import React, { useState, useRef, useEffect } from "react";
import { icons } from "lucide-react";
import api from "~/services/api";
import { UnreadBadge, useTicketUnread } from "./useTicketUnread";
import TicketForm from "./TicketForm";
import TicketList from "./TicketList";
import TicketDetail from "./TicketDetail";

const MessageSquare = icons["MessageSquareMore"];
const X = icons["X"];
const Send = icons["Send"];
const Bot = icons["Bot"];
const UserIcon = icons["User"];
const PhoneCall = icons["PhoneCall"];
const Loader = icons["Loader"];
const TicketIcon = icons["Ticket"];
const Plus = icons["Plus"];

const C = {
  orange: "#ee5827",
  primary: "#27306c",
  secondary: "rgba(232, 234, 241, 1)",
  secondaryDark: "rgba(210, 214, 228, 1)",
  white: "#ffffff",
  textDark: "#1f2937",
  textMid: "#6b7280",
};

function renderMarkdown(text) {
  return text.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} style={{ height: 4 }} />;

    const numberedMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
    if (numberedMatch) {
      return (
        <div key={i} style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <span style={{ fontWeight: 700, color: C.orange, flexShrink: 0 }}>{numberedMatch[1]}.</span>
          <span>{parseBold(numberedMatch[2])}</span>
        </div>
      );
    }

    const bulletMatch = trimmed.match(/^[-•]\s+(.*)/);
    if (bulletMatch) {
      return (
        <div key={i} style={{ display: "flex", gap: 6, marginTop: 4 }}>
          <span style={{ color: C.orange, flexShrink: 0 }}>•</span>
          <span>{parseBold(bulletMatch[1])}</span>
        </div>
      );
    }

    return <p key={i} style={{ marginTop: i === 0 ? 0 : 4 }}>{parseBold(trimmed)}</p>;
  });
}

function parseBold(text) {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i} style={{ color: C.primary }}>{part}</strong> : part
  );
}

const headerButton = {
  position: "relative",
  width: 36,
  height: 36,
  borderRadius: 10,
  border: "none",
  background: "rgba(255,255,255,0.12)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [hardEscalation, setHardEscalation] = useState(false);
  const unread = useTicketUnread((state) => state.count);
  const [view, setView] = useState("chat");
  const [ticketId, setTicketId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [me, setMe] = useState({ holding: false, approver: false });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const loadMe = () =>
    api.get("/tickets/settings").then(({ data }) => setMe(data.me)).catch(() => {});

  useEffect(() => {
    if (open) loadMe();
  }, [open]);

  useEffect(() => {
    function handleToggle() {
      setOpen((v) => !v);
    }
    function handleOpenTickets() {
      setView("list");
      setOpen(true);
    }
    window.addEventListener("support-chat:toggle", handleToggle);
    window.addEventListener("support-chat:open-tickets", handleOpenTickets);
    return () => {
      window.removeEventListener("support-chat:toggle", handleToggle);
      window.removeEventListener("support-chat:open-tickets", handleOpenTickets);
    };
  }, []);

  async function sendMessage(e) {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading || hardEscalation) return;

    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    try {
      const { data } = await api.post("/support-agent/chat", {
        messages: newMessages,
      });

      setMessages([...newMessages, { role: "assistant", content: data.reply }]);
      if (data.escalated) setEscalated(true);
      if (data.hardEscalation) setHardEscalation(true);
    } catch {
      setMessages([...newMessages, {
        role: "assistant",
        content: "Something went wrong. Please try again.",
        error: true,
      }]);
    } finally {
      setLoading(false);
    }
  }

  function openTicket(id) {
    setTicketId(id);
    setView("detail");
  }

  function startTicketFromChat() {
    const question = [...messages].reverse().find((m) => m.role === "user")?.content || "";
    const answer = [...messages].reverse().find((m) => m.role === "assistant")?.content || "";
    setDraft({
      title: question.slice(0, 120),
      description: question + (answer ? "\n\nChatbot answer:\n" + answer : ""),
    });
    setView("new");
  }

  function goTo(next) {
    if (next !== "new") setDraft(null);
    setView(next);
  }

  const ticketBody = {
    new: <TicketForm draft={draft} onCreated={(id) => { setDraft(null); openTicket(id); }} />,
    list: <TicketList canReview={me.holding || me.approver} onOpen={openTicket} />,
    detail: <TicketDetail key={ticketId} id={ticketId} onBack={() => setView("list")} />,
  }[view];

  function handleReset() {
    setMessages([]);
    setEscalated(false);
    setHardEscalation(false);
    setInput("");
  }

  return (
    <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12 }}>

      {/* Chat window */}
      {open && (
        <div style={{ width: 520, height: 700, maxHeight: "calc(100vh - 100px)", display: "flex", flexDirection: "column", borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", border: `1px solid ${C.secondaryDark}`, background: C.white }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: C.primary }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Bot size={18} color={C.white} />
              <span style={{ fontWeight: 600, fontSize: 13, color: C.white }}>MILA Support</span>
              {escalated && !hardEscalation && (
                <span style={{ fontSize: 10, background: C.orange, color: C.white, padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>
                  Escalated
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {view !== "chat" && (
                <button title="Chat" onClick={() => goTo("chat")} style={headerButton}>
                  <Bot size={22} color={C.white} />
                </button>
              )}
              <button title="New ticket" onClick={() => goTo("new")} style={headerButton}>
                <Plus size={24} color={view === "new" ? C.orange : C.white} />
              </button>
              <button title="Tickets" onClick={() => goTo("list")} style={headerButton}>
                <TicketIcon size={22} color={view === "list" || view === "detail" ? C.orange : C.white} />
                <UnreadBadge count={unread} className="absolute -top-1 -right-1" />
              </button>
              {view === "chat" && messages.length > 0 && (
                <button onClick={handleReset} style={{ fontSize: 11, color: C.secondary, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                  New chat
                </button>
              )}
              <button title="Close" onClick={() => setOpen(false)} style={headerButton}>
                <X size={22} color={C.white} />
              </button>
            </div>
          </div>

          {view !== "chat" ? ticketBody : (<>
          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "12px", display: "flex", flexDirection: "column", gap: 8, background: C.secondary }}>
            {messages.length === 0 && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 8, textAlign: "center" }}>
                <Bot size={32} color={C.secondaryDark} />
                <p style={{ fontSize: 13, fontWeight: 600, color: C.primary, margin: 0 }}>How can I help you today?</p>
                <p style={{ fontSize: 11, color: C.textMid, margin: 0 }}>Ask me anything about MILA Plus.</p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-end", gap: 6, flexDirection: msg.role === "user" ? "row-reverse" : "row" }}>
                <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: msg.role === "user" ? C.orange : C.primary }}>
                  {msg.role === "user" ? <UserIcon size={13} color={C.white} /> : <Bot size={13} color={C.white} />}
                </div>
                <div style={{
                  maxWidth: "78%", padding: "8px 12px", borderRadius: 14, fontSize: 12, lineHeight: 1.6,
                  ...(msg.role === "user"
                    ? { background: C.orange, color: C.white, borderBottomRightRadius: 3 }
                    : msg.error
                      ? { background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderBottomLeftRadius: 3 }
                      : { background: C.white, color: C.textDark, border: `1px solid ${C.secondaryDark}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", borderBottomLeftRadius: 3 })
                }}>
                  {msg.role === "assistant" && !msg.error ? renderMarkdown(msg.content) : msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
                <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: C.primary }}>
                  <Bot size={13} color={C.white} />
                </div>
                <div style={{ background: C.white, border: `1px solid ${C.secondaryDark}`, borderRadius: 14, borderBottomLeftRadius: 3, padding: "8px 12px" }}>
                  <Loader size={14} color={C.orange} style={{ animation: "spin 1s linear infinite" }} />
                </div>
              </div>
            )}

            {escalated && (
              <button type="button" onClick={startTicketFromChat} style={{ alignSelf: "center", display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, color: C.white, background: C.primary, border: "none", borderRadius: 99, padding: "6px 14px", cursor: "pointer" }}>
                <TicketIcon size={13} color={C.white} /> Open a ticket
              </button>
            )}

            {hardEscalation && (
              <div style={{ margin: "4px 0", padding: 12, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 12, display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11, color: "#9a3412" }}>
                <PhoneCall size={14} color={C.orange} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Our AI couldn't fully resolve your issue. Please contact support directly at <strong>support@mila.com</strong>.</span>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderTop: `1px solid ${C.secondaryDark}`, background: C.white }}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading || hardEscalation}
              placeholder={hardEscalation ? "Chat ended — contact support" : "Type your message..."}
              style={{ flex: 1, fontSize: 12, padding: "8px 12px", borderRadius: 10, border: `1px solid ${C.secondaryDark}`, background: C.secondary, outline: "none", color: C.textDark, opacity: (loading || hardEscalation) ? 0.5 : 1 }}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading || hardEscalation}
              style={{ width: 34, height: 34, borderRadius: 10, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: input.trim() && !loading && !hardEscalation ? C.orange : C.secondaryDark, flexShrink: 0, transition: "background 0.15s" }}
            >
              <Send size={14} color={C.white} />
            </button>
          </form>
          </>)}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{ position: "relative", width: 52, height: 52, borderRadius: "50%", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: C.orange, boxShadow: "0 4px 16px rgba(238,88,39,0.4)", transition: "transform 0.15s" }}
        onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.08)"}
        onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
        title="Support Chat"
      >
        {open ? <X size={22} color={C.white} /> : <MessageSquare size={22} color={C.white} />}
        {!open && <UnreadBadge count={unread} className="absolute -top-1 -right-1" />}
      </button>
    </div>
  );
}
